import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// ── Shared cell style ─────────────────────────────────────────────────────────
const TD = ({ children, mono = false, bold = false, className = '' }) => (
    <td className={`px-3 py-2 text-xs border-b border-gray-800 text-gray-300 ${mono ? 'font-mono' : ''} ${bold ? 'font-bold' : ''} ${className}`}>
        {children}
    </td>
);

const TH = ({ children }) => (
    <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-700">
        {children}
    </th>
);

// ── Color dot for phase type ──────────────────────────────────────────────────
const PHASE_COLORS = {
    'Early-Race': '#3b82f6',
    'Mid-Race': '#22c55e',
    'Late-Race': '#f97316',
    'Last Spurt': '#ef4444',
};

const SLOPE_COLORS = {
    'Up': '#f87171',
    'Down': '#60a5fa',
};

const ColorDot = ({ color }) => (
    <span
        style={{ backgroundColor: color }}
        className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 flex-shrink-0"
    />
);

/**
 * CourseMapData
 * Collapsible raw data panel showing all segment numbers in tabular form.
 *
 * Props:
 *   segments {object}  Same segments object used by CourseMapTrack
 *   meta     {object}  { meters, terrain, course, direction }
 */
const CourseMapData = ({ segments, meta }) => {
    const [open, setOpen] = useState(false);

    if (!segments || !meta) return null;

    const { Phases, Corners, Slopes, Straights, Spurt, Thresholds } = segments;

    const slopeData = Slopes?.filter(
        s => s.slope_type && s.slope_type !== 'None' && s.slope_value !== 0
    ) || [];

    return (
        <div className="mt-4 border border-gray-700/50 rounded-xl overflow-hidden">

            {/* Toggle header */}
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-800/60 hover:bg-gray-800 transition-colors text-left"
            >
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                    Raw Segment Data
                </span>
                {open
                    ? <ChevronUp className="w-4 h-4 text-gray-500" />
                    : <ChevronDown className="w-4 h-4 text-gray-500" />
                }
            </button>

            {open && (
                <div className="p-4 space-y-6 bg-gray-900/40">

                    {/* ── Phases ── */}
                    {Phases?.length > 0 && (
                        <section>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Phases</p>
                            <div className="overflow-x-auto rounded-lg">
                                <table className="w-full border-collapse">
                                    <thead className="bg-gray-800/80">
                                        <tr>
                                            <TH>Phase</TH>
                                            <TH>Start</TH>
                                            <TH>End</TH>
                                            <TH>Length</TH>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Phases.map((p) => (
                                            <tr key={p.type} className="hover:bg-gray-800/40 transition-colors">
                                                <TD bold>
                                                    <div className="flex items-center">
                                                        <ColorDot color={PHASE_COLORS[p.type] || '#6b7280'} />
                                                        {p.type}
                                                    </div>
                                                </TD>
                                                <TD mono>{p.start}m</TD>
                                                <TD mono>{p.end}m</TD>
                                                <TD mono>{p.end - p.start}m</TD>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* ── Corners ── */}
                    {Corners?.filter(c => c.start !== null).length > 0 && (
                        <section>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Corners</p>
                            <div className="overflow-x-auto rounded-lg">
                                <table className="w-full border-collapse">
                                    <thead className="bg-gray-800/80">
                                        <tr>
                                            <TH>#</TH>
                                            <TH>Start</TH>
                                            <TH>End</TH>
                                            <TH>Length</TH>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Corners.filter(c => c.start !== null).map((c, i) => (
                                            <tr key={i} className="hover:bg-gray-800/40 transition-colors">
                                                <TD bold>
                                                    <div className="flex items-center">
                                                        <ColorDot color="#facc15" />
                                                        Corner {c.corner_number ?? i + 1}
                                                    </div>
                                                </TD>
                                                <TD mono>{c.start}m</TD>
                                                <TD mono>{c.end}m</TD>
                                                <TD mono>{c.end - c.start}m</TD>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* ── Slopes ── */}
                    {slopeData.length > 0 && (
                        <section>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Slopes</p>
                            <div className="overflow-x-auto rounded-lg">
                                <table className="w-full border-collapse">
                                    <thead className="bg-gray-800/80">
                                        <tr>
                                            <TH>Type</TH>
                                            <TH>Intensity</TH>
                                            <TH>Start</TH>
                                            <TH>End</TH>
                                            <TH>Length</TH>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {slopeData.map((s, i) => (
                                            <tr key={i} className="hover:bg-gray-800/40 transition-colors">
                                                <TD bold>
                                                    <div className="flex items-center">
                                                        <ColorDot color={SLOPE_COLORS[s.slope_type] || '#9ca3af'} />
                                                        {s.slope_type}
                                                    </div>
                                                </TD>
                                                <TD mono className="text-gray-400">
                                                    {s.slope_value > 0 ? `+${s.slope_value}` : s.slope_value}
                                                </TD>
                                                <TD mono>{s.start}m</TD>
                                                <TD mono>{s.end}m</TD>
                                                <TD mono>{s.end - s.start}m</TD>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* ── Straights ── */}
                    {Straights?.length > 0 && (
                        <section>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Straights</p>
                            <div className="overflow-x-auto rounded-lg">
                                <table className="w-full border-collapse">
                                    <thead className="bg-gray-800/80">
                                        <tr>
                                            <TH>#</TH>
                                            <TH>Start</TH>
                                            <TH>End</TH>
                                            <TH>Length</TH>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Straights.map((s, i) => (
                                            <tr key={i} className="hover:bg-gray-800/40 transition-colors">
                                                <TD bold>
                                                    <div className="flex items-center">
                                                        <ColorDot color="#34d399" />
                                                        Straight {i + 1}
                                                    </div>
                                                </TD>
                                                <TD mono>{s.start}m</TD>
                                                <TD mono>{s.end}m</TD>
                                                <TD mono>{s.end - s.start}m</TD>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* ── Spurt ── */}
                    {Spurt && (
                        <section>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Spurt Zone</p>
                            <div className="flex flex-wrap gap-4 text-xs font-mono bg-gray-800/60 rounded-lg px-4 py-3">
                                <span className="text-gray-400">Start: <span className="text-gray-200">{Spurt.start}m</span></span>
                                <span className="text-gray-400">End: <span className="text-gray-200">{Spurt.end}m</span></span>
                                <span className="text-gray-400">Length: <span className="text-gray-200">{Spurt.end - Spurt.start}m</span></span>
                                {Spurt.description && Spurt.description !== 'None' && (
                                    <span className="text-gray-400">Type: <span className="text-purple-300 font-bold">{Spurt.description}</span></span>
                                )}
                            </div>
                        </section>
                    )}

                    {/* ── Thresholds ── */}
                    {Thresholds?.stats?.length > 0 && (
                        <section>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Stat Thresholds Required</p>
                            <div className="flex flex-wrap gap-2">
                                {Thresholds.stats.map(stat => (
                                    <span
                                        key={stat}
                                        className="px-3 py-1 rounded-lg bg-violet-900/50 text-violet-200 border border-violet-700/50 font-mono text-xs font-bold"
                                    >
                                        {stat}
                                    </span>
                                ))}
                            </div>
                        </section>
                    )}

                </div>
            )}
        </div>
    );
};

export default CourseMapData;
